import { AxiosResponse } from "axios";

import { axiosInstance } from "./common";

export enum EntityType {
  CASE = "CASE",
  LOAN_ACCOUNT = "LOAN_ACCOUNT",
  STAKEHOLDER = "STAKEHOLDER",
  ACCOUNT = "ACCOUNT",
}

export enum RelatedAs {
  BORROWER = "BORROWER",
  PRIMARY_BORROWER = "PRIMARY_BORROWER",
  CO_BORROWER = "CO_BORROWER",
  GUARANTOR = "GUARANTOR",
  EMPLOYEE = "EMPLOYEE",
  FORMER_EMPLOYEE = "FORMER_EMPLOYEE",
  SALES_MANAGER = "SALES_MANAGER",
  SANCTIONING_MANAGER = "SANCTIONING_MANAGER",
  FIELD_OFFICER = "FIELD_OFFICER",
  UNDERWRITER = "UNDERWRITER",
  LEGAL_OFFICER = "LEGAL_OFFICER",
  RELATIONSHIP_MANAGER = "RELATIONSHIP_MANAGER",
  SOURCING_PARTNER = "SOURCING_PARTNER",
  SELLER = "SELLER",
  CONNECTOR = "CONNECTOR",
  DEALER = "DEALER",
  DIRECT_SALES_AGENT = "DIRECT_SALES_AGENT",
  BUILDER = "BUILDER",
  BROKER = "BROKER",
  VENDOR = "VENDOR",
  OTHER = "OTHER",
  OTHERS_EMPLOYEE = "OTHERS_EMPLOYEE",
  OTHERS_OUTSIDER = "OTHERS_OUTSIDER",
  PROPRIETOR = "PROPRIETOR",
  PARTNER = "PARTNER",
  DIRECTOR = "DIRECTOR",
  PROMOTER = "PROMOTER",
  TRUSTEE = "TRUSTEE",
  AUTHORIZED_SIGNATORY = "AUTHORIZED_SIGNATORY",

  // Account-holder side (deposit / CASA / cyber)
  PRIMARY_ACCOUNT_HOLDER = "PRIMARY_ACCOUNT_HOLDER",
  JOINT_ACCOUNT_HOLDER = "JOINT_ACCOUNT_HOLDER",
  BENEFICIARY_ACCOUNT_HOLDER = "BENEFICIARY_ACCOUNT_HOLDER",
  NOMINEE = "NOMINEE",
  MANDATE_HOLDER = "MANDATE_HOLDER",
  POWER_OF_ATTORNEY_HOLDER = "POWER_OF_ATTORNEY_HOLDER",
  BRANCH_MANAGER = "BRANCH_MANAGER",

  // Onboarding / servicing staff
  KYC_OFFICER = "KYC_OFFICER",
  CUSTOMER_SERVICE_OFFICER = "CUSTOMER_SERVICE_OFFICER",
  TELLER = "TELLER",

  // Channels / correspondents
  BUSINESS_CORRESPONDENT = "BUSINESS_CORRESPONDENT",
  BUSINESS_CORRESPONDENT_AGENT = "BUSINESS_CORRESPONDENT_AGENT",

  // Cheque fraud
  DRAWER = "DRAWER",
  PAYEE = "PAYEE",

  // ATM fraud
  ATM_CUSTODIAN = "ATM_CUSTODIAN",
  CASH_REPLENISHMENT_AGENCY = "CASH_REPLENISHMENT_AGENCY",

  // Currency chest / vault
  CHEST_CUSTODIAN = "CHEST_CUSTODIAN",
  HEAD_CASHIER = "HEAD_CASHIER",

  // Cyber / privileged access
  IT_STAFF = "IT_STAFF",
  SYSTEM_ADMIN = "SYSTEM_ADMIN",
  THIRD_PARTY_FRAUDSTER = "THIRD_PARTY_FRAUDSTER",
  EXTERNAL_ATTACKER = "EXTERNAL_ATTACKER",
}

export const RELATED_AS_LABELS: Record<RelatedAs, string> = {
  [RelatedAs.BORROWER]: "Borrower",
  [RelatedAs.PRIMARY_BORROWER]: "Primary Borrower",
  [RelatedAs.CO_BORROWER]: "Co-Borrower",
  [RelatedAs.GUARANTOR]: "Guarantor",
  [RelatedAs.EMPLOYEE]: "Employee",
  [RelatedAs.FORMER_EMPLOYEE]: "Former Employee",
  [RelatedAs.SALES_MANAGER]: "Sales Manager",
  [RelatedAs.SANCTIONING_MANAGER]: "Sanctioning Manager",
  [RelatedAs.FIELD_OFFICER]: "Field Officer",
  [RelatedAs.UNDERWRITER]: "Underwriter",
  [RelatedAs.LEGAL_OFFICER]: "Legal Officer",
  [RelatedAs.RELATIONSHIP_MANAGER]: "Relationship Manager",
  [RelatedAs.SOURCING_PARTNER]: "Sourcing Partner",
  [RelatedAs.SELLER]: "Seller",
  [RelatedAs.CONNECTOR]: "Connector",
  [RelatedAs.DEALER]: "Dealer",
  [RelatedAs.DIRECT_SALES_AGENT]: "Direct Sales Agent",
  [RelatedAs.BUILDER]: "Builder",
  [RelatedAs.BROKER]: "Broker",
  [RelatedAs.VENDOR]: "Vendor",
  [RelatedAs.OTHER]: "Other",
  [RelatedAs.OTHERS_EMPLOYEE]: "Other (Employee)",
  [RelatedAs.OTHERS_OUTSIDER]: "Other (Outsider)",
  [RelatedAs.PROPRIETOR]: "Proprietor",
  [RelatedAs.PARTNER]: "Partner",
  [RelatedAs.DIRECTOR]: "Director",
  [RelatedAs.PROMOTER]: "Promoter",
  [RelatedAs.TRUSTEE]: "Trustee",
  [RelatedAs.AUTHORIZED_SIGNATORY]: "Authorized Signatory",
  [RelatedAs.PRIMARY_ACCOUNT_HOLDER]: "Primary Account Holder",
  [RelatedAs.JOINT_ACCOUNT_HOLDER]: "Joint Account Holder",
  [RelatedAs.BENEFICIARY_ACCOUNT_HOLDER]: "Beneficiary Account Holder",
  [RelatedAs.NOMINEE]: "Nominee",
  [RelatedAs.MANDATE_HOLDER]: "Mandate Holder",
  [RelatedAs.POWER_OF_ATTORNEY_HOLDER]: "Power of Attorney Holder",
  [RelatedAs.BRANCH_MANAGER]: "Branch Manager",
  [RelatedAs.KYC_OFFICER]: "KYC Officer",
  [RelatedAs.CUSTOMER_SERVICE_OFFICER]: "Customer Service Officer",
  [RelatedAs.TELLER]: "Teller",
  [RelatedAs.BUSINESS_CORRESPONDENT]: "Business Correspondent",
  [RelatedAs.BUSINESS_CORRESPONDENT_AGENT]: "BC / CSP Agent",
  [RelatedAs.DRAWER]: "Drawer",
  [RelatedAs.PAYEE]: "Payee",
  [RelatedAs.ATM_CUSTODIAN]: "ATM Custodian",
  [RelatedAs.CASH_REPLENISHMENT_AGENCY]: "CRA / Cash-loading Vendor",
  [RelatedAs.CHEST_CUSTODIAN]: "Chest Custodian",
  [RelatedAs.HEAD_CASHIER]: "Head Cashier",
  [RelatedAs.IT_STAFF]: "IT Staff",
  [RelatedAs.SYSTEM_ADMIN]: "System Admin",
  [RelatedAs.THIRD_PARTY_FRAUDSTER]: "Third Party Fraudster",
  [RelatedAs.EXTERNAL_ATTACKER]: "External Attacker",
};

export const KEY_PERSONNEL_RELATED_AS: ReadonlySet<RelatedAs> = new Set([
  RelatedAs.PROPRIETOR,
  RelatedAs.PARTNER,
  RelatedAs.DIRECTOR,
  RelatedAs.PROMOTER,
  RelatedAs.TRUSTEE,
  RelatedAs.AUTHORIZED_SIGNATORY,
]);

export const RELATED_AS_BY_ENTITY_TYPE: Record<EntityType, RelatedAs[]> = {
  [EntityType.LOAN_ACCOUNT]: [
    RelatedAs.BORROWER,
    RelatedAs.PRIMARY_BORROWER,
    RelatedAs.CO_BORROWER,
    RelatedAs.GUARANTOR,
    RelatedAs.EMPLOYEE,
    RelatedAs.FORMER_EMPLOYEE,
    RelatedAs.SALES_MANAGER,
    RelatedAs.SANCTIONING_MANAGER,
    RelatedAs.FIELD_OFFICER,
    RelatedAs.UNDERWRITER,
    RelatedAs.LEGAL_OFFICER,
    RelatedAs.RELATIONSHIP_MANAGER,
    RelatedAs.OTHERS_EMPLOYEE,
  ],

  [EntityType.STAKEHOLDER]: Array.from(KEY_PERSONNEL_RELATED_AS),

  [EntityType.ACCOUNT]: [
    RelatedAs.PRIMARY_ACCOUNT_HOLDER,
    RelatedAs.JOINT_ACCOUNT_HOLDER,
    RelatedAs.BENEFICIARY_ACCOUNT_HOLDER,
    RelatedAs.NOMINEE,
    RelatedAs.MANDATE_HOLDER,
    RelatedAs.POWER_OF_ATTORNEY_HOLDER,
    RelatedAs.EMPLOYEE,
    RelatedAs.FORMER_EMPLOYEE,
    RelatedAs.RELATIONSHIP_MANAGER,
    RelatedAs.SALES_MANAGER,
    RelatedAs.BRANCH_MANAGER,
    RelatedAs.KYC_OFFICER,
    RelatedAs.CUSTOMER_SERVICE_OFFICER,
    RelatedAs.TELLER,
    RelatedAs.BUSINESS_CORRESPONDENT,
    RelatedAs.BUSINESS_CORRESPONDENT_AGENT,
    RelatedAs.DRAWER,
    RelatedAs.PAYEE,
    RelatedAs.ATM_CUSTODIAN,
    RelatedAs.CASH_REPLENISHMENT_AGENCY,
    RelatedAs.CHEST_CUSTODIAN,
    RelatedAs.HEAD_CASHIER,
    RelatedAs.IT_STAFF,
    RelatedAs.SYSTEM_ADMIN,
    RelatedAs.THIRD_PARTY_FRAUDSTER,
    RelatedAs.EXTERNAL_ATTACKER,
    RelatedAs.VENDOR,
    RelatedAs.PROPRIETOR,
    RelatedAs.PARTNER,
    RelatedAs.DIRECTOR,
    RelatedAs.PROMOTER,
    RelatedAs.TRUSTEE,
    RelatedAs.AUTHORIZED_SIGNATORY,
    RelatedAs.OTHER,
    RelatedAs.OTHERS_EMPLOYEE,
    RelatedAs.OTHERS_OUTSIDER,
  ],

  [EntityType.CASE]: Object.values(RelatedAs),
};

export interface StakeholderRelationshipFields {
  entity_type: EntityType;
  entity_id: string;
  related_as: RelatedAs;
}

export interface StakeholderRelationship
  extends StakeholderRelationshipFields {
  relationship_id: string;
  stakeholder_id: string;
}

export const StakeholderRelationshipsAPI = {
  add: async (
    case_id: string,
    stakeholder_id: string,
    request: StakeholderRelationshipFields,
  ): Promise<StakeholderRelationship> => {
    const response: AxiosResponse<StakeholderRelationship> =
      await axiosInstance.post(
        `/v1/cases/${case_id}/stakeholders/${stakeholder_id}/relationships`,
        request,
      );

    return response.data;
  },
};